import {fetchPlaceAddressById, loadGoogleMaps, mapGeocoderResultToAddressLookupResult} from './googleAddressService'
import {getCantonsByCountry} from 'utils/cantonUtils'

export interface SwissCantonAddress {
    streetAddress?: string
    postalCode?: string
    city?: string
}

const cantonCodes = new Set(getCantonsByCountry('CH')
    .filter((option) => option.country === 'CH' && /^[A-Z]{2}$/.test(option.value))
    .map((option) => option.value))
const normalizeCity = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\bsankt\b/g, 'st').replace(/[^a-z0-9]/g, '')
const pendingLookups = new Map<string, Promise<string | null>>()

/** Resolve a canton, never infer one from a postal-code range or an arbitrary first match. */
export const lookupSwissCanton = (address: SwissCantonAddress): Promise<string | null> => {
    const postalCode = address.postalCode?.trim() || ''
    const city = address.city?.trim() || ''
    const street = address.streetAddress?.trim() || ''
    if (!city || (postalCode ? !/^\d{4}$/.test(postalCode) : !street)) {
        return Promise.resolve(null)
    }

    // Postal locality also works for old CRM addresses with an inaccurate street name.
    const query = postalCode ? city : `${street}, ${city}`
    const key = JSON.stringify([postalCode, query.toLowerCase()])
    const existing = pendingLookups.get(key)
    if (existing) return existing

    const lookup = (async () => {
        try {
            await loadGoogleMaps()
            const {AutocompleteSuggestion, AutocompleteSessionToken} = await google.maps.importLibrary('places') as google.maps.PlacesLibrary
            const {suggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: postalCode || query,
                includedRegionCodes: ['ch'],
                includedPrimaryTypes: postalCode ? ['postal_code'] : ['street_address', 'premise', 'route'],
                sessionToken: new AutocompleteSessionToken(),
            })
            const results = await Promise.all(suggestions.map((suggestion) =>
                suggestion.placePrediction?.placeId
                    ? fetchPlaceAddressById(suggestion.placePrediction.placeId)
                    : Promise.resolve(null)))
            if (results.some((result) => !result)) return null
            const matches = new Set<string>()
            for (const result of results) {
                if (!result) continue
                const resolved = mapGeocoderResultToAddressLookupResult(result)
                const code = resolved.region.toUpperCase()
                if (resolved.country_code.toUpperCase() !== 'CH'
                    || (postalCode && resolved.postcode !== postalCode)
                    || (!postalCode && normalizeCity(resolved.city) !== normalizeCity(city))
                    || !cantonCodes.has(code)) continue
                matches.add(code)
            }
            return matches.size === 1 ? [...matches][0] : null
        } catch {
            // Lookup is optional: keep manual canton selection available on API/network failure.
            return null
        }
    })().finally(() => pendingLookups.delete(key))
    pendingLookups.set(key, lookup)
    return lookup
}
